import React, { useState, useRef, useEffect } from 'react';

import {CSS} from '../../../render/renderUtils';

interface RydDialProps {
    min: number;
    max: number;
    initialValue?: number;
    update?: (value: number) => void;
    label?: string
}

const START_ANGLE = -135;
const END_ANGLE = 135;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
    const rad = (angleDeg - 90) * Math.PI / 180.0;
    return {
        x: cx + (r * Math.cos(rad)),
        y: cy + (r * Math.sin(rad))
    };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
    const start = polarToCartesian(cx, cy, r, startAngle);
    const end = polarToCartesian(cx, cy, r, endAngle);
    const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
    const sweepFlag = endAngle >= startAngle ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`;
}

export function RydDial({
    min,
    max,
    initialValue,
    update = (_) => {},
    label
} : RydDialProps) {
    const [value, setValue] = useState<number>(
        initialValue !== undefined ? initialValue : (min + max) / 2
    );
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editText, setEditText] = useState<string>(value.toString());

    const dialRef = useRef<HTMLDivElement | null>(null);
    const startYRef = useRef<number>(0);
    const startValueRef = useRef<number>(value);

    useEffect(() => {
        update(value);
    }, [value]);

    const valueToAngle = (val: number) => {
        const ratio = (val - min) / (max - min);
        return ratio * 270 - 135;
    };

    const handleMouseDown = (event: React.MouseEvent) => {
        startYRef.current = event.clientY;
        startValueRef.current = value;
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (event: MouseEvent) => {
        const deltaY = startYRef.current - event.clientY;
        const sensitivity = (max - min) / 100;
        let newValue = startValueRef.current + deltaY * sensitivity;
        newValue = Math.min(Math.max(newValue, min), max);
        setValue(newValue);
        setEditText(newValue.toFixed(2));
    };

    const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const handleNumberClick = () => {
        setIsEditing(true);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEditText(event.target.value);
    };

    const commitInput = () => {
        const parsed = parseFloat(editText);
        if (!isNaN(parsed)) {
            const clamped = Math.min(Math.max(parsed, min), max);
            setValue(clamped);
            setEditText(clamped.toString());
        }
        setIsEditing(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            commitInput();
        }
    };

    const size = 48;
    const center = size / 2;
    const radius = 16;
    const angle = valueToAngle(value);
    const progress = (value - min) / (max - min);

    const arcPath = describeArc(center, center, radius, START_ANGLE, END_ANGLE);

    return (<>
        <div style={{ textAlign: 'center', userSelect: 'none' }}>
            <div ref={dialRef} onMouseDown={handleMouseDown} style={{ cursor: 'grab', display: 'inline-block' }}>
                <svg width={size} height={size}>
                    {/* Track (full 270° arc) */}
                    <path
                        d={arcPath}
                        stroke="#888"
                        strokeWidth="4"
                        fill="none"
                        strokeLinecap="round"
                    />
                    {/* Progress up to current value */}
                    <path
                        d={arcPath}
                        // stroke="#3b82f6"
                        stroke={CSS.VAR('--vi-p-13')}
                        strokeWidth="4"
                        fill="none"
                        strokeLinecap="round"
                        pathLength={100}
                        strokeDasharray={`${Math.max(0, Math.min(1, progress)) * 100} 100`}
                    />
                    {/* Knob/handle */}
                    <line
                        x1={center}
                        y1={center}
                        x2={center}
                        y2={center - radius}
                        stroke="#444"
                        strokeWidth="4"
                        transform={`rotate(${angle}, ${center}, ${center})`}
                    />
                </svg>
            </div>
            <div style={{ marginBottom: '2px', cursor: 'pointer', alignItems: 'left' }}>
                {isEditing ? (
                    <input
                        type="number"
                        value={editText}
                        onChange={handleInputChange}
                        onBlur={commitInput}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        style={{ textAlign: 'center' }}
                    />
                ) : (
                    <span onClick={handleNumberClick} style={{ fontSize: '0.7em' }}>
                        {label ? <b>{`${label}`}</b> : null} {value.toFixed(2)}
                    </span>
                )}
            </div>
        </div>
        </>
    );
};
